/**
 * Footbar Bridge for Dawn Variant Picker
 * Misell theme - high-variant support
 *
 * Syncs footbar Dawn variant picker changes to update the hidden variant ID input
 * so cart add works correctly from the footbar.
 */
(function() {
  'use strict';

  let productJsonCache = null;
  let requestSerial = 0;
  let pendingVariantPromise = null;
  let pendingVariantResolve = null;
  let pendingVariantRequestId = 0;
  let isAutoClicking = false;
  const footbarUnlockTimers = new WeakMap();
  let cartAddedListenerBound = false;
  let footbarVariantDirty = false;

  function isValidVariantId(value) {
    return /^\d+$/.test(String(value || ''));
  }

  function beginVariantRequest(requestId) {
    pendingVariantRequestId = requestId;
    pendingVariantPromise = new Promise(resolve => {
      pendingVariantResolve = resolve;
    });
  }

  function endVariantRequest(requestId) {
    if (pendingVariantResolve && pendingVariantRequestId === requestId) {
      pendingVariantResolve();
      pendingVariantResolve = null;
      pendingVariantPromise = null;
    }
  }

  function getFootbarVariantId(footbarPicker) {
    const formLinkedInput = document.querySelector('input[name="id"][form="footbar-product-form"]');
    if (formLinkedInput && isValidVariantId(formLinkedInput.value)) {
      return String(formLinkedInput.value);
    }

    const footbarForm = document.getElementById('footbar-product-form');
    const formInput = footbarForm ? footbarForm.querySelector('input[name="id"]') : null;
    if (formInput && isValidVariantId(formInput.value)) {
      return String(formInput.value);
    }

    const dawnInput = footbarPicker ? footbarPicker.querySelector('[data-dawn-variant-id]') : null;
    if (dawnInput && isValidVariantId(dawnInput.value)) {
      return String(dawnInput.value);
    }

    const selectedVariantEl = footbarPicker ? footbarPicker.querySelector('[data-selected-variant]') : null;
    if (selectedVariantEl) {
      try {
        const parsed = JSON.parse(selectedVariantEl.textContent);
        if (parsed && isValidVariantId(parsed.id)) {
          return String(parsed.id);
        }
      } catch (e) {
        // ignore parse error
      }
    }

    return '';
  }

  function collectFootbarSelectedOptions(footbarPicker) {
    const options = [];

    if (!footbarPicker) return { selectedOptions: options, optionValueIds: [] };

    footbarPicker.querySelectorAll('select').forEach(select => {
      const selectedOption = select.selectedOptions && select.selectedOptions[0];
      if (!selectedOption) return;

      const position = parseInt(selectedOption.dataset.optionPosition || select.dataset.optionPosition || 0, 10);
      options.push({
        name: selectedOption.dataset.optionName || select.name,
        position: position,
        value: selectedOption.value,
        optionValueId: selectedOption.dataset.optionValueId || ''
      });
    });

    footbarPicker.querySelectorAll('input[type="radio"]:checked').forEach(input => {
      const position = parseInt(input.dataset.optionPosition || 0, 10);
      options.push({
        name: input.dataset.optionName || input.name,
        position: position,
        value: input.value,
        optionValueId: input.dataset.optionValueId || ''
      });
    });

    const sortedOptions = options.sort((a, b) => a.position - b.position);
    const optionValueIds = sortedOptions.map(opt => opt.optionValueId).filter(Boolean);

    return { selectedOptions: sortedOptions, optionValueIds };
  }

  function ensureFootbarVariantResolved(footbarPicker) {
    const currentId = getFootbarVariantId(footbarPicker);
    if (isValidVariantId(currentId) && !footbarVariantDirty) {
      return { pending: false };
    }

    if (pendingVariantPromise && pendingVariantRequestId === requestSerial) {
      return { pending: true, promise: pendingVariantPromise };
    }

    const productUrl = footbarPicker?.dataset?.productUrl;
    if (!productUrl) return { pending: false };

    const { selectedOptions, optionValueIds } = collectFootbarSelectedOptions(footbarPicker);
    if (!selectedOptions.length) return { pending: false };

    fetchVariantFromSectionAPI(productUrl, optionValueIds, selectedOptions, footbarPicker);
    if (pendingVariantPromise && pendingVariantRequestId === requestSerial) {
      return { pending: true, promise: pendingVariantPromise };
    }

    return { pending: false };
  }

  function lockFootbarButton(button) {
    if (!button) return;
    button.dataset.footbarLocked = 'true';
    button.classList.add('loading');
    button.setAttribute('aria-busy', 'true');
  }

  function unlockFootbarButton(button) {
    if (!button) return;
    delete button.dataset.footbarLocked;
    button.classList.remove('loading');
    button.removeAttribute('aria-busy');

    const timerId = footbarUnlockTimers.get(button);
    if (timerId) {
      clearTimeout(timerId);
      footbarUnlockTimers.delete(button);
    }
  }

  function scheduleFootbarUnlock(button, delayMs) {
    if (!button) return;
    const timerId = setTimeout(() => {
      unlockFootbarButton(button);
    }, delayMs);
    footbarUnlockTimers.set(button, timerId);
  }

  function bindFootbarAddToCartClick(footbarPicker) {
    const footbarProduct = footbarPicker.closest('.js-footbar-product');
    if (!footbarProduct) return;

    footbarProduct.addEventListener('click', function(event) {
      const button = event.target.closest('[data-js-product-button-add-to-cart]');
      if (!button) return;

      if (isAutoClicking) {
        isAutoClicking = false;
        return;
      }

      if (button.dataset.footbarLocked === 'true') {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      const status = ensureFootbarVariantResolved(footbarPicker);
      const resolvedId = getFootbarVariantId(footbarPicker);
      const hasValidId = isValidVariantId(resolvedId);

      if (!hasValidId || status.pending) {
        event.preventDefault();
        event.stopImmediatePropagation();

        lockFootbarButton(button);
        scheduleFootbarUnlock(button, 3500);

        const waitPromise = status.pending && status.promise
          ? Promise.race([
              status.promise,
              new Promise(resolve => setTimeout(resolve, 2000))
            ])
          : Promise.resolve();

        waitPromise.then(() => {
          const latestId = getFootbarVariantId(footbarPicker);
          if (!isValidVariantId(latestId)) {
            unlockFootbarButton(button);
            return;
          }

          const footbarForm = document.getElementById('footbar-product-form');
          if (footbarForm) {
            footbarForm.querySelectorAll('input[name="id"]').forEach(input => {
              input.value = latestId;
            });
          }
          document.querySelectorAll('input[name="id"][form="footbar-product-form"]').forEach(input => {
            input.value = latestId;
          });

          isAutoClicking = true;
          button.click();
        });
        return;
      }

      lockFootbarButton(button);
      scheduleFootbarUnlock(button, 3500);
    }, true);
  }

  // Wait for DOM and pub-sub to be ready
  function initFootbarBridge() {
    const footbarPicker = document.querySelector('.js-footbar-product variant-selects');
    if (!footbarPicker) {
      console.log('[FootbarBridge] No footbar variant picker found');
      return;
    }

    console.log('[FootbarBridge] Initializing footbar bridge');
    if (!footbarPicker.dataset.footbarAddToCartBound) {
      bindFootbarAddToCartClick(footbarPicker);
      footbarPicker.dataset.footbarAddToCartBound = 'true';
    }

    if (!cartAddedListenerBound) {
      document.addEventListener('theme:cart::added', function(event) {
        const button = event?.detail?.button;
        if (!button || !button.closest('.js-footbar-product')) return;
        unlockFootbarButton(button);
      });
      cartAddedListenerBound = true;
    }

    // Load product JSON asynchronously
    loadProductJson().then(productJson => {
      if (!productJson) {
        console.error('[FootbarBridge] Could not find product JSON');
        return;
      }

      productJsonCache = productJson;

      // Subscribe to option value selection changes
      const canUsePubSub =
        typeof subscribe === 'function' &&
        typeof PUB_SUB_EVENTS !== 'undefined' &&
        PUB_SUB_EVENTS.optionValueSelectionChange;
      if (canUsePubSub) {
        subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, function(event) {
          handleFootbarOptionChange(event, productJson, footbarPicker);
        });
        console.log('[FootbarBridge] Subscribed to optionValueSelectionChange');
      } else {
        // Fallback: listen to change events directly
        console.log('[FootbarBridge] Using fallback change listener');
        footbarPicker.addEventListener('change', function(e) {
          if (e.target.type === 'hidden') return;
          handleFootbarChangeEvent(e, productJson, footbarPicker);
        });
      }
    });
  }

  async function loadProductJson() {
    // Try to get product JSON from main product element
    const mainProduct = document.querySelector('[data-js-product-clone-id="footbar"]');
    if (mainProduct) {
      const jsonAttr = mainProduct.getAttribute('data-json-product');
      if (jsonAttr) {
        try {
          // Check if it's inline JSON or a URL
          if (jsonAttr.startsWith('{')) {
            return JSON.parse(jsonAttr);
          } else {
            // It's a URL, fetch the JSON
            console.log('[FootbarBridge] Fetching product JSON from:', jsonAttr);
            const response = await fetch(jsonAttr);
            return await response.json();
          }
        } catch (e) {
          console.error('[FootbarBridge] Error loading product JSON:', e);
        }
      }
    }

    // Fallback: try to get from footbar's data-selected-variant
    const footbarVariantJson = document.querySelector('.js-footbar-product [data-selected-variant]');
    if (footbarVariantJson) {
      try {
        const variant = JSON.parse(footbarVariantJson.textContent);
        // Return minimal structure with current variant
        return { variants: [variant], currentVariant: variant };
      } catch (e) {
        console.error('[FootbarBridge] Error parsing footbar variant JSON:', e);
      }
    }

    return null;
  }

  function handleFootbarOptionChange(event, productJson, footbarPicker) {
    const { data } = event;
    if (!data || !data.event) return;

    // Only handle changes from footbar picker
    if (!footbarPicker.contains(data.event.target)) {
      console.log('[FootbarBridge] Change not from footbar, ignoring');
      return;
    }

    console.log('[FootbarBridge] Footbar option changed:', data.selectedOptions);
    console.log('[FootbarBridge] selectedOptionValues:', data.selectedOptionValues);
    footbarVariantDirty = true;

    // For high-variant products, use Section Rendering API to get variant
    const productUrl = footbarPicker.dataset.productUrl;
    if (productUrl && data.selectedOptionValues) {
      fetchVariantFromSectionAPI(productUrl, data.selectedOptionValues, data.selectedOptions, footbarPicker);
    } else {
      // Fallback: try local lookup (works for < 100 variants)
      const variant = findVariantByOptions(productJson, data.selectedOptions);
      if (variant) {
        console.log('[FootbarBridge] Found variant locally:', variant.id, variant.title);
        updateMainProductVariant(variant);
        updateFootbarVariant(variant, footbarPicker);
      } else {
        console.log('[FootbarBridge] No matching variant found locally');
      }
    }
  }

  async function fetchVariantFromSectionAPI(productUrl, optionValueIds, selectedOptions, footbarPicker) {
    const params = [];
    const requestId = ++requestSerial;
    beginVariantRequest(requestId);

    // Disable Add to Cart button during fetch to prevent timing issues
    const footbarProduct = footbarPicker.closest('.js-footbar-product');
    const addToCartBtn = footbarProduct?.querySelector('[data-js-product-button-add-to-cart]');
    if (addToCartBtn) {
      addToCartBtn.setAttribute('disabled', 'disabled');
      addToCartBtn.classList.add('loading');
    }

    // Use option_values if available (Dawn v15+ pattern)
    const hasAllOptionValueIds = optionValueIds.length && optionValueIds.every(Boolean);
    if (hasAllOptionValueIds) {
      params.push(`option_values=${optionValueIds.join(',')}`);
    } else {
      // Fallback: send option1/2/3 params
      selectedOptions.forEach((opt) => {
        if (opt.position) {
          params.push(`option${opt.position}=${encodeURIComponent(opt.value)}`);
        }
      });
    }

    // Use main-product section to get variant JSON
    params.push('section_id=main-product');

    const requestUrl = `${productUrl}?${params.join('&')}`;
    console.log('[FootbarBridge] Fetching variant from:', requestUrl);

    try {
      const response = await fetch(requestUrl);
      const html = await response.text();
      if (requestId !== requestSerial) {
        console.log('[FootbarBridge] Stale response ignored');
        return;
      }

      // Parse the response to get variant JSON
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Try to find variant JSON in response
      const variantJson = doc.querySelector('[data-selected-variant]');
      if (variantJson) {
        const variant = JSON.parse(variantJson.textContent);
        if (requestId !== requestSerial) return;
        console.log('[FootbarBridge] Found variant via API:', variant.id, variant.title);
        updateMainProductVariant(variant);
        updateFootbarVariant(variant, footbarPicker);
        return;
      }

      // Alternative: parse variant from URL or input
      const variantInput = doc.querySelector('input[name="id"]');
      if (variantInput && variantInput.value) {
        const variantId = parseInt(variantInput.value, 10);
        if (requestId !== requestSerial) return;
        console.log('[FootbarBridge] Found variant ID via input:', variantId);
        updateFootbarVariantById(variantId, footbarPicker, productUrl);
        return;
      }

      // MISEll theme pattern: check data-product-variant-id attribute
      const productVariantElement = doc.querySelector('[data-product-variant-id]');
      if (productVariantElement && productVariantElement.dataset.productVariantId) {
        const variantId = parseInt(productVariantElement.dataset.productVariantId, 10);
        if (requestId !== requestSerial) return;
        console.log('[FootbarBridge] Found variant ID via data-product-variant-id:', variantId);
        updateFootbarVariantById(variantId, footbarPicker, productUrl);
        return;
      }

      console.log('[FootbarBridge] Could not find variant in API response');
      // Re-enable button even if variant not found
      if (addToCartBtn) {
        addToCartBtn.removeAttribute('disabled');
        addToCartBtn.classList.remove('loading');
      }
    } catch (error) {
      console.error('[FootbarBridge] Error fetching variant:', error);
      // Re-enable button on error
      if (addToCartBtn) {
        addToCartBtn.removeAttribute('disabled');
        addToCartBtn.classList.remove('loading');
      }
    } finally {
      endVariantRequest(requestId);
    }
  }

  function updateFootbarVariantById(variantId, footbarPicker, productUrl) {
    console.log('[FootbarBridge] updateFootbarVariantById called with:', variantId);

    // Update the hidden input directly with variant ID
    const footbarFormVariantInput = document.getElementById('footbar-variant-id-input');
    if (footbarFormVariantInput) {
      console.log('[FootbarBridge] Updating #footbar-variant-id-input to:', variantId);
      footbarFormVariantInput.value = variantId;
    }

    // Update Dawn hidden input
    const dawnInput = footbarPicker.querySelector('[data-dawn-variant-id]');
    if (dawnInput) {
      dawnInput.value = variantId;
    }

    // Update all input[name="id"] in footbar form
    const footbarForm = document.getElementById('footbar-product-form');
    if (footbarForm) {
      footbarForm.querySelectorAll('input[name="id"]').forEach(input => {
        input.value = variantId;
      });
    }

    // Update inputs linked to the form via the form attribute
    document.querySelectorAll('input[name="id"][form="footbar-product-form"]').forEach(input => {
      input.value = variantId;
    });

    // Update browser URL with variant parameter
    if (productUrl) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      window.history.replaceState({}, '', url.toString());
      console.log('[FootbarBridge] Updated URL to:', url.toString());
    }

    // Sync main product variant inputs
    const mainProduct = document.querySelector('[data-js-product-clone-id="footbar"]');
    if (mainProduct) {
      // Update main product hidden inputs
      mainProduct.querySelectorAll('input[name="id"][type="hidden"]').forEach(input => {
        input.value = variantId;
      });
      mainProduct.setAttribute('data-product-variant-id', variantId);

      // Update legacy variant input
      const variantInput = mainProduct.querySelector('[data-js-product-variant-input]');
      if (variantInput) {
        variantInput.value = variantId;
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      // Trigger jQuery variant:change event for gallery and label updates
      if (typeof jQueryTheme !== 'undefined') {
        console.log('[FootbarBridge] Triggering variant:change event');
        jQueryTheme(mainProduct).trigger('variant:change', [variantId]);
      }
    }

    // Sync footbar labels to main product labels
    syncFootbarLabelsToMain(footbarPicker);
    footbarVariantDirty = false;

    // Re-enable footbar add to cart button after variant ID update
    const footbarProduct = footbarPicker.closest('.js-footbar-product');
    if (footbarProduct) {
      footbarProduct.setAttribute('data-product-variant-id', variantId);
      const addToCartBtn = footbarProduct.querySelector('[data-js-product-button-add-to-cart]');
      if (addToCartBtn) {
        addToCartBtn.removeAttribute('disabled');
        addToCartBtn.classList.remove('loading');
      }
    }
  }

  function syncFootbarLabelsToMain(footbarPicker) {
    // Get selected options from footbar
    const footbarSelects = footbarPicker.querySelectorAll('select:not([type="hidden"])');
    const selectedOptions = [];

    footbarSelects.forEach(select => {
      if (select.selectedOptions[0]) {
        const position = parseInt(select.dataset.optionPosition || 0, 10);
        selectedOptions.push({
          position: position,
          value: select.selectedOptions[0].value
        });
      }
    });

    // Also check radio inputs
    footbarPicker.querySelectorAll('input[type="radio"]:checked').forEach(input => {
      const position = parseInt(input.dataset.optionPosition || 0, 10);
      selectedOptions.push({
        position: position,
        value: input.value
      });
    });

    // Sort by position
    selectedOptions.sort((a, b) => a.position - b.position);

    // Update main product option labels
    const mainProduct = document.querySelector('[data-js-product-clone-id="footbar"]');
    if (mainProduct) {
      // Directly find all [data-label-value] elements and update by index
      const labelValueElements = mainProduct.querySelectorAll('[data-label-value]');

      selectedOptions.forEach((opt, index) => {
        // Update [data-label-value] span directly by index
        if (labelValueElements[index]) {
          labelValueElements[index].textContent = opt.value;
          console.log('[FootbarBridge] Updated label [data-label-value] index', index, 'to:', opt.value);
        }
      });

      // Update active state on option values (if they exist)
      const optionValueGroups = mainProduct.querySelectorAll('[data-js-product-options] > div');
      selectedOptions.forEach((opt, index) => {
        const optionValues = optionValueGroups[index]?.querySelectorAll('[data-js-option-value]');
        optionValues?.forEach(optEl => {
          if (optEl.dataset.value === opt.value) {
            optEl.classList.add('active');
          } else {
            optEl.classList.remove('active');
          }
        });
      });

      // Update combined variant selector display (e.g., "MediumBlue / S")
      const combinedDisplay = mainProduct.querySelector('[data-selected-display]');
      if (combinedDisplay && selectedOptions.length > 0) {
        const combinedText = selectedOptions.map(o => o.value).join(' / ');
        combinedDisplay.textContent = combinedText;
        console.log('[FootbarBridge] Updated combined display to:', combinedText);
      }

      // Also update the text span inside custom-variant-selector
      const selectorText = mainProduct.querySelector('.custom-variant-selector__text');
      if (selectorText && selectedOptions.length > 0) {
        const combinedText = selectedOptions.map(o => o.value).join(' / ');
        selectorText.textContent = combinedText;
      }

      // Update selected state in dropdown options
      const dropdownOptions = mainProduct.querySelectorAll('.custom-variant-selector__option');
      if (dropdownOptions.length > 0 && selectedOptions.length > 0) {
        const targetText = selectedOptions.map(o => o.value).join(' / ');
        dropdownOptions.forEach(opt => {
          if (opt.textContent.trim() === targetText) {
            opt.classList.add('selected');
          } else {
            opt.classList.remove('selected');
          }
        });
      }
    }
  }

  function handleFootbarChangeEvent(event, productJson, footbarPicker) {
    // Get selected options from footbar picker
    const selectedOptions = getSelectedOptionsFromPicker(footbarPicker);
    console.log('[FootbarBridge] Fallback - Selected options:', selectedOptions);
    footbarVariantDirty = true;

    // Find matching variant
    const variant = findVariantByOptions(productJson, selectedOptions);
    if (variant) {
      console.log('[FootbarBridge] Found variant:', variant.id, variant.title);
      updateMainProductVariant(variant);
      updateFootbarVariant(variant, footbarPicker);
    }
  }

  function getSelectedOptionsFromPicker(picker) {
    const options = [];

    // Check selects
    picker.querySelectorAll('select').forEach(select => {
      const position = parseInt(select.dataset.optionPosition || 0, 10);
      if (select.selectedOptions[0]) {
        options.push({
          name: select.name,
          position: position,
          value: select.selectedOptions[0].value
        });
      }
    });

    // Check radio inputs
    picker.querySelectorAll('input[type="radio"]:checked').forEach(input => {
      const position = parseInt(input.dataset.optionPosition || 0, 10);
      options.push({
        name: input.dataset.optionName || input.name,
        position: position,
        value: input.value
      });
    });

    return options.sort((a, b) => a.position - b.position);
  }

  function findVariantByOptions(productJson, selectedOptions) {
    if (!productJson) return null;

    const variants = productJson.variants;
    if (!variants || !variants.length) return null;

    // Build option values array sorted by position
    const sortedOptions = [...selectedOptions].sort((a, b) => a.position - b.position);
    const optionValues = sortedOptions.map(opt => opt.value);

    console.log('[FootbarBridge] Looking for variant with options:', optionValues);

    // Find variant matching all options
    return variants.find(variant => {
      return optionValues.every((value, index) => {
        const optionKey = `option${index + 1}`;
        return variant[optionKey] === value;
      });
    });
  }

  function updateMainProductVariant(variant) {
    // Update main product's hidden variant input
    const mainProduct = document.querySelector('[data-js-product-clone-id="footbar"]');
    if (!mainProduct) {
      console.log('[FootbarBridge] Main product element not found');
      return;
    }

    // Update legacy variant input
    const variantInput = mainProduct.querySelector('[data-js-product-variant-input]');
    if (variantInput) {
      console.log('[FootbarBridge] Updating main product variant input to:', variant.id);
      variantInput.value = variant.id;
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Update Dawn variant input if present
    const dawnInput = mainProduct.querySelector('input[data-dawn-variant-id]');
    if (dawnInput) {
      console.log('[FootbarBridge] Updating main product Dawn input to:', variant.id);
      dawnInput.value = variant.id;
      dawnInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Update hidden id inputs
    mainProduct.querySelectorAll('input[name="id"][type="hidden"]').forEach(input => {
      input.value = variant.id;
    });

    // Update legacy variant select if it exists and has the option (may not for 250+ variants)
    const variantSelect = mainProduct.querySelector('[data-js-product-variants]');
    if (variantSelect && variantSelect.querySelector(`option[value="${variant.id}"]`)) {
      variantSelect.value = variant.id;
    }

    // Update product-variant-id attribute
    mainProduct.setAttribute('data-product-variant-id', variant.id);

    // Trigger legacy theme variant switch if available
    if (typeof jQueryTheme !== 'undefined') {
      const $mainProduct = jQueryTheme(mainProduct);
      // Update button state based on availability
      const $addToCartBtn = $mainProduct.find('[data-js-product-button-add-to-cart]');
      if ($addToCartBtn.length) {
        if (variant.available) {
          $addToCartBtn.removeAttr('disabled').attr('data-button-status', '');
        } else {
          $addToCartBtn.attr('disabled', 'disabled').attr('data-button-status', 'sold-out');
        }
      }
    }
  }

  function updateFootbarVariant(variant, footbarPicker) {
    if (!footbarPicker) return;

    console.log('[FootbarBridge] Updating footbar variant to:', variant.id);

    // Update footbar's hidden variant input (inside variant-selects)
    const footbarVariantInput = footbarPicker.querySelector('[data-dawn-variant-id]');
    if (footbarVariantInput) {
      console.log('[FootbarBridge] Updating footbar Dawn input');
      footbarVariantInput.value = variant.id;
    }

    // Update footbar form's hidden input (name="id") - this is the one jQuery serializeArray uses
    // Priority: Use the direct form input by ID
    const footbarFormVariantInput = document.getElementById('footbar-variant-id-input');
    if (footbarFormVariantInput) {
      console.log('[FootbarBridge] Updating #footbar-variant-id-input to:', variant.id);
      footbarFormVariantInput.value = variant.id;
    }

    // Also update any other input[name="id"] in the footbar form
    const footbarForm = document.getElementById('footbar-product-form');
    if (footbarForm) {
      footbarForm.querySelectorAll('input[name="id"]').forEach(input => {
        console.log('[FootbarBridge] Updating form input[name="id"] to:', variant.id);
        input.value = variant.id;
      });
    }

    // Update inputs linked to the form via the form attribute
    document.querySelectorAll('input[name="id"][form="footbar-product-form"]').forEach(input => {
      input.value = variant.id;
    });

    // Update footbar's selected variant JSON
    const variantJson = footbarPicker.querySelector('[data-selected-variant]');
    if (variantJson) {
      variantJson.textContent = JSON.stringify(variant);
    }

    // Update browser URL with variant parameter
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
    console.log('[FootbarBridge] Updated URL to:', url.toString());

    // Trigger jQuery variant:change event for gallery and label updates
    const mainProduct = document.querySelector('[data-js-product-clone-id="footbar"]');
    if (mainProduct && typeof jQueryTheme !== 'undefined') {
      console.log('[FootbarBridge] Triggering variant:change event');
      jQueryTheme(mainProduct).trigger('variant:change', [variant.id]);
    }

    // Sync footbar labels to main product labels
    syncFootbarLabelsToMain(footbarPicker);
    footbarVariantDirty = false;

    // Update footbar add to cart button state
    const footbarProduct = footbarPicker.closest('.js-footbar-product');
    if (footbarProduct) {
      footbarProduct.setAttribute('data-product-variant-id', variant.id);
      const addToCartBtn = footbarProduct.querySelector('[data-js-product-button-add-to-cart]');
      if (addToCartBtn) {
        // Always remove loading state after variant update
        addToCartBtn.classList.remove('loading');
        if (variant.available) {
          addToCartBtn.removeAttribute('disabled');
          addToCartBtn.removeAttribute('data-button-status');
        } else {
          addToCartBtn.setAttribute('disabled', 'disabled');
          addToCartBtn.setAttribute('data-button-status', 'sold-out');
        }
      }
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFootbarBridge);
  } else {
    // Give a small delay for other scripts to load
    setTimeout(initFootbarBridge, 100);
  }

  // Also initialize on theme ready if available
  if (typeof theme !== 'undefined' && theme.AssetsLoader) {
    theme.AssetsLoader.onPageLoaded(initFootbarBridge);
  }
})();
